<template>
  <div class="product-detail">
    <fn-add-to-cart-modal
      v-if="modalAddToCartVisible"
      :product="product"
      :entry="entries.entry"
      :added-quantity="entries.quantityAdded"
      @close="modalAddToCartVisible = false"
    />

    <fn-modal-product-sizes-and-measurements
      v-if="modalSizesAndMeasurementsVisible && hasSizeTablesOrMeasureInfo"
      keep-scroll-position
      :size-table="product?.sizeTable?.table"
      :size-table-measurement="product?.sizeTableMeasurement?.table"
      :measurements-info="product?.measureInfo"
      @close="modalSizesAndMeasurementsVisible = false"
    />

    <fn-section
      class="show-m"
      constrained="l"
      padding-bottom="xs"
      padding-top="xs"
    >
      <fn-breadcrumbs :nodes="adjustedBreadcrumbs" />
    </fn-section>

    <fn-section
      constrained="l"
      padding-top="xs"
      padding-bottom="xs"
    >
      <fn-product-overview
        :nodes="breadcrumbs"
        :product="product"
        @add-to-cart="$emit('addToCart', $event)"
        @add-to-cart-overlay="handleAddToCartModal($event)"
        @change-tab="(tab, insideTab) => scrollTo(tab, insideTab)"
        @change-variant="$emit('changeVariant', $event)"
        @modal-sizes-and-measurements-opened="modalSizesAndMeasurementsVisible = true"
        @set-active-tab="(tab) => (activeTab = tab)"
      />
    </fn-section>

    <fn-section
      v-if="curalateGalleryEnabled"
      constrained="l"
    >
      <fn-curalate-gallery
        container-id="product"
        :filter="`productId:'${product.code}'`"
        :lazy-load="curalateGalleryLazyLoad"
      />
    </fn-section>

    <fn-adobe-target-mbox-wrapper
      v-slot="{ visible }"
      @insert-reco="handleInsertReco"
    >
      <fn-section
        v-show="visible"
        constrained="l"
        padding-top="s"
        padding-bottom="s"
      >
        <fn-adobe-target-mbox id="recoSlot1" />
      </fn-section>
    </fn-adobe-target-mbox-wrapper>

    <fn-adobe-target-mbox-wrapper
      v-slot="{ visible }"
      @insert-reco="handleInsertReco"
    >
      <fn-section
        v-show="visible"
        constrained="l"
        padding-top="xs"
        padding-bottom="xs"
      >
        <fn-adobe-target-mbox id="recoSlot2" />
      </fn-section>
    </fn-adobe-target-mbox-wrapper>

    <fn-section
      constrained="l"
      padding-top="s"
      padding-bottom="s"
    >
      <fn-product-details
        ref="productDetails"
        :product="product"
        :active-tab="activeTab"
        @change-tab="(tab) => scrollTo(tab)"
        @set-active-tab="(tab) => (activeTab = tab)"
        @modal-sizes-and-measurements-opened="modalSizesAndMeasurementsVisible = true"
      />
    </fn-section>

    <fn-adobe-target-mbox-wrapper
      v-slot="{ visible }"
      @insert-reco="handleInsertReco"
    >
      <fn-section
        v-show="visible"
        constrained="l"
        padding-top="s"
        padding-bottom="s"
      >
        <fn-adobe-target-mbox id="recoSlot3" />
      </fn-section>
    </fn-adobe-target-mbox-wrapper>

    <fn-adobe-target-mbox-wrapper
      v-slot="{ visible }"
      @insert-reco="handleInsertReco"
    >
      <fn-section
        v-show="visible"
        constrained="l"
        padding-top="xs"
        padding-bottom="xs"
      >
        <fn-adobe-target-mbox id="recoSlot4" />
      </fn-section>
    </fn-adobe-target-mbox-wrapper>

    <fn-product-reviews
      :page="page"
      :product="product"
      :reviews-script="reviewsScript"
    />

    <fn-seo-related-links
      v-if="page?.content?.related_links?.length > 1"
      :related-links="page.content.related_links"
    />

    <fn-adobe-target-mbox-wrapper v-slot="{ visible }">
      <fn-section
        v-show="visible"
        constrained="l"
        padding-top="s"
        padding-bottom="s"
      >
        <fn-adobe-target-mbox id="recoSlot5" />
      </fn-section>
    </fn-adobe-target-mbox-wrapper>

    <fn-adobe-target-mbox-wrapper v-slot="{ visible }">
      <fn-section
        v-show="visible"
        constrained="l"
        padding-top="xs"
        padding-bottom="m"
      >
        <fn-adobe-target-mbox id="recoSlot6" />
      </fn-section>
    </fn-adobe-target-mbox-wrapper>

    <fn-cms-section-loader
      v-if="page?.content?.content_slots?.a?.sections"
      :sections="page.content.content_slots.a.sections"
    />

    <fn-section
      v-if="page?.content?.category"
      constrained="l"
      padding-top="s"
      padding-bottom="s"
    >
      <fn-headline type="h2">
        {{ $t('product.ourBrands') }}
      </fn-headline>
      <fn-brand-slider :slides="page.content.category.brands" />
    </fn-section>

    <fn-cms-section-loader
      v-if="page?.content?.content_slots?.b?.sections"
      :sections="page.content.content_slots.b.sections"
    />
  </div>
</template>

<script>
import PageScroll from '@/mixins/PageScroll';

export default {
  mixins: [PageScroll],
  props: {
    page: {
      type: Object,
      required: true,
    },
    product: {
      type: Object,
      required: true,
    },
    reviewsScript: {
      type: String,
      default: '',
    },
    breadcrumbs: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['addToCart', 'changeVariant'],
  data() {
    return {
      activeTab: null,
      modalAddToCartVisible: false,
      modalSizesAndMeasurementsVisible: false,
      addedQuantity: 0,
      entries: null,
    };
  },
  computed: {
    ...mapState(useMainStore, {
      curalateGalleryEnabled: 'curalateGalleryEnabled',
      curalateGalleryLazyLoad: 'curalateGalleryLazyLoad',
    }),
    isSearchResult() {
      return this.$route.query?.isSearchPage !== undefined || this.$route.query?.q !== undefined;
    },
    adjustedBreadcrumbs() {
      if (!this.isSearchResult) {
        return this.breadcrumbs;
      }

      let url = '';
      if (this.$route.query?.isSearchPage) {
        url = `/search/?text=${this.$route.query?.isSearchPage}`;
      } else if (this.$route.query?.q) {
        url = `/search/?q=${this.$route.query?.q}`;
      }

      const breadcrumbs = [
        {
          id: '',
          target: '',
          title: this.$t('search.title'),
          url,
        },
        this.breadcrumbs[this.breadcrumbs.length - 1],
      ];

      return breadcrumbs;
    },
    hasSizeTablesOrMeasureInfo() {
      return (
        !!Object.keys(this.product?.measureInfo || {}).length ||
        !!this.product?.sizeTable?.table?.length ||
        !!this.product?.sizeTableMeasurement?.table?.length
      );
    },
  },
  mounted() {
    this.handleHashScrollTo();
    this.sanitizeUrlQuery();
  },
  methods: {
    handleAddToCartModal(payload) {
      if (payload) {
        this.entries = payload;
        this.modalAddToCartVisible = true;
      }
    },

    scrollTo(content, insideContent = null) {
      if (!content) {
        this.activeTab = '';
        return;
      }

      let el = this.$refs.productDetails.$refs[content].$el;

      if (!el) {
        return;
      }

      if (insideContent) {
        const insideEl = this.$refs.productDetails.$refs[insideContent].$el;

        if (insideEl) {
          el = insideEl;
        }
      }

      this.activeTab = content;

      this.$nextTick(() => {
        el.scrollIntoView({
          behavior: 'smooth',
        });
      });
    },

    handleInsertReco() {
      this.handleHashScrollTo();
    },

    sanitizeUrlQuery() {
      if (this.$route.query?.q?.length) {
        const currentQuery = { ...this.$route.query };
        delete currentQuery.q;

        this.$router.replace({ query: currentQuery });
      }
    },
  },
};
</script>
